import type { Friend, FriendRequest } from '@runway/shared';
import { useMemo, useState } from 'react';
import { ApiError } from '../../net/api';
import { useHub } from '../../state/hub';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/Card';
import { Modal } from '../../ui/Modal';
import { toast } from '../../ui/Toast';
import { cx } from '../../ui/cx';
import { AddFriendBox } from './AddFriendBox';
import { UserAvatar } from './UserAvatar';
import './friends.css';

export function FriendsPage() {
  const friends = useHub((s) => s.friends);
  const incoming = useHub((s) => s.incoming);
  const outgoing = useHub((s) => s.outgoing);
  const online = useHub((s) => s.online);
  const loaded = useHub((s) => s.loaded.friends);
  const refreshError = useHub((s) => s.lastRefreshError);
  const refresh = useHub((s) => s.refresh);

  const onlineCount = friends.reduce((n, f) => n + (online.has(f.id) ? 1 : 0), 0);

  return (
    <div className="stack" style={{ gap: 'var(--sp-6)' }}>
      <header className="row row-between">
        <div>
          <div className="eyebrow">Squad</div>
          <h1 style={{ margin: 0 }}>Friends</h1>
        </div>
        {friends.length > 0 ? (
          <span className={cx('chip', onlineCount > 0 && 'chip-success')}>
            <span className={cx('online-dot', onlineCount > 0 && 'on')} /> {onlineCount} of {friends.length} online
          </span>
        ) : null}
      </header>

      {refreshError ? (
        <div className="card card-tight row row-between" role="alert">
          <span className="help help-error">{refreshError}</span>
          <Button size="sm" variant="ghost" onClick={() => void refresh()}>
            Retry
          </Button>
        </div>
      ) : null}

      <div className="friends-layout">
        <div className="stack">
          {incoming.length > 0 ? (
            <Card title={`Friend requests (${incoming.length})`}>
              {incoming.map((r) => (
                <IncomingRow key={r.id} req={r} />
              ))}
            </Card>
          ) : null}

          <Card title="Your friends">
            <FriendList friends={friends} loaded={loaded} />
            {outgoing.length > 0 ? (
              <>
                <div className="friends-group-title">Waiting for a reply</div>
                {outgoing.map((r) => (
                  <OutgoingRow key={r.id} req={r} />
                ))}
              </>
            ) : null}
          </Card>
        </div>

        <aside className="friends-side stack">
          <Card title="Add a friend">
            <AddFriendBox autoFocus={friends.length === 0 && incoming.length === 0} />
            <p className="help" style={{ marginTop: 'var(--sp-3)' }}>
              Friends can invite you to shows and send you messages. You will both need to say yes.
            </p>
          </Card>
        </aside>
      </div>
    </div>
  );
}

// ── rows ───────────────────────────────────────────────────────────────────

function FriendList({ friends, loaded }: { friends: Friend[]; loaded: boolean }) {
  const online = useHub((s) => s.online);
  const sorted = useMemo(
    () =>
      [...friends].sort((a, b) => {
        const oa = online.has(a.id) ? 1 : 0;
        const ob = online.has(b.id) ? 1 : 0;
        if (oa !== ob) return ob - oa;
        return a.displayName.localeCompare(b.displayName);
      }),
    [friends, online],
  );

  if (!loaded && friends.length === 0) {
    return (
      <div className="stack stack-sm" aria-busy="true" aria-label="Loading friends">
        {[0, 1, 2].map((i) => (
          <div key={i} className="skeleton" style={{ height: 58 }} />
        ))}
      </div>
    );
  }

  if (friends.length === 0) {
    return (
      <div className="empty">
        <div className="empty-emoji" aria-hidden="true">
          👯
        </div>
        <h4>No friends yet</h4>
        <p>Add one by name or email — the box is right over there. Then you can invite them to your shows and chat.</p>
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 2 }}>
      {sorted.map((f) => (
        <FriendRow key={f.id} friend={f} online={online.has(f.id)} />
      ))}
    </div>
  );
}

function FriendRow({ friend, online }: { friend: Friend; online: boolean }) {
  const openChat = useHub((s) => s.openChat);
  const removeFriend = useHub((s) => s.removeFriend);
  const unread = useHub((s) => s.unread[friend.id] ?? 0);
  const [confirm, setConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);

  const remove = async () => {
    setRemoving(true);
    try {
      await removeFriend(friend.friendshipId);
      toast.info(`Removed ${friend.displayName}`);
      setConfirm(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="friend-row">
      <UserAvatar displayName={friend.displayName} picture={friend.picture} avatar={friend.avatar} online={online} />
      <div style={{ minWidth: 0 }}>
        <div className="friend-row-name">{friend.displayName}</div>
        <div className={cx('friend-row-sub', online && 'on')}>{online ? 'Online now' : `Friends since ${new Date(friend.since).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}</div>
      </div>
      <div className="friend-row-actions">
        <Button size="sm" variant={unread > 0 ? 'primary' : 'ghost'} onClick={() => openChat(friend.id)}>
          Message{unread > 0 ? ` (${unread})` : ''}
        </Button>
        <button type="button" className="btn-icon-ghost" aria-label={`Remove ${friend.displayName}`} title="Remove friend" onClick={() => setConfirm(true)}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
          </svg>
        </button>
      </div>
      <Modal open={confirm} onClose={() => setConfirm(false)} title={`Remove ${friend.displayName}?`} size="sm" locked={removing}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(false)} disabled={removing}>
              Keep
            </Button>
            <Button variant="danger" onClick={() => void remove()} loading={removing}>
              Remove
            </Button>
          </>
        }
      >
        <p className="dim">You will stop seeing each other online and cannot message or invite each other until you are friends again.</p>
      </Modal>
    </div>
  );
}

function IncomingRow({ req }: { req: FriendRequest }) {
  const acceptFriend = useHub((s) => s.acceptFriend);
  const declineFriend = useHub((s) => s.declineFriend);
  const [busy, setBusy] = useState<'accept' | 'decline' | null>(null);

  const act = async (kind: 'accept' | 'decline') => {
    setBusy(kind);
    try {
      if (kind === 'accept') {
        await acceptFriend(req.id);
        toast.success(`You and ${req.from.displayName} are now friends!`);
      } else {
        await declineFriend(req.id);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Something went wrong');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="request-row">
      <UserAvatar displayName={req.from.displayName} picture={req.from.picture} avatar={req.from.avatar} />
      <div style={{ minWidth: 0 }}>
        <div className="friend-row-name">{req.from.displayName}</div>
        <div className="friend-row-sub">wants to be friends</div>
      </div>
      <div className="request-row-actions">
        <Button size="sm" variant="gold" loading={busy === 'accept'} disabled={busy === 'decline'} onClick={() => void act('accept')}>
          Accept
        </Button>
        <Button size="sm" variant="ghost" loading={busy === 'decline'} disabled={busy === 'accept'} onClick={() => void act('decline')}>
          Decline
        </Button>
      </div>
    </div>
  );
}

function OutgoingRow({ req }: { req: FriendRequest }) {
  const cancelRequest = useHub((s) => s.cancelRequest);
  const [busy, setBusy] = useState(false);
  return (
    <div className="friend-row">
      <UserAvatar displayName={req.to.displayName} picture={req.to.picture} avatar={req.to.avatar} />
      <div style={{ minWidth: 0 }}>
        <div className="friend-row-name">{req.to.displayName}</div>
        <div className="friend-row-sub">Request sent · waiting for them to accept</div>
      </div>
      <div className="friend-row-actions">
        <Button
          size="sm"
          variant="ghost"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await cancelRequest(req.id);
            } catch (err) {
              toast.error(err instanceof ApiError ? err.message : 'Could not cancel');
            } finally {
              setBusy(false);
            }
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
